import { useEffect, useMemo, useState } from "react";
import { Check, X, Mail, Phone, RefreshCcw } from "lucide-react";
import { RolePill, CONSULTATION_SUBTABS } from "./shared";

const ConsultationRow = ({ row, onToggle, onNotes, onDelete }) => {
  const [notes, setNotes] = useState(row.notes || "");
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  // Keep textarea in sync when the row updates from elsewhere (refresh, toggle).
  useEffect(() => {
    setNotes(row.notes || "");
  }, [row.notes]);

  const handleBlur = async () => {
    if ((notes || "") === (row.notes || "")) return;
    setSaving(true);
    try {
      await onNotes(row, notes);
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 1400);
    } finally {
      setSaving(false);
    }
  };

  const fmtDate = row.date
    ? new Date(row.date + "T00:00:00").toLocaleDateString()
    : "—";
  const past =
    !row.completed &&
    row.date &&
    row.date < new Date().toISOString().slice(0, 10);
  return (
    <div
      data-testid={`consultation-row-${row.source}-${row.source_id}`}
      className={`group flex items-start gap-4 px-5 py-4 border-b border-[var(--ld-border)]/70 last:border-b-0 transition-colors ${
        row.completed
          ? "bg-[var(--ld-ink)]/[0.02]"
          : "hover:bg-[var(--ld-ink)]/[0.02]"
      }`}
    >
      <button
        data-testid={`consultation-toggle-${row.source}-${row.source_id}`}
        onClick={() => onToggle(row)}
        aria-label={
          row.completed ? "Mark as pending" : "Mark consultation complete"
        }
        className={`mt-1 inline-flex h-5 w-5 items-center justify-center border transition-colors ${
          row.completed
            ? "bg-[var(--ld-ink)] border-[var(--ld-ink)] text-[var(--ld-gold)]"
            : "border-[var(--ld-ink)]/40 hover:border-[var(--ld-gold-deep)]"
        }`}
      >
        {row.completed && <Check size={13} strokeWidth={2} />}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p
            className={`text-[15px] text-[var(--ld-ink)] ${
              row.completed ? "line-through opacity-60" : ""
            }`}
          >
            {row.name || "—"}
            {row.company && (
              <span className="text-[var(--ld-ink-muted)]"> · {row.company}</span>
            )}
          </p>
          <RolePill role={row.role} />
          {row.source === "signup" && (
            <span className="inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase border border-orange-700/40 text-orange-700">
              Consultation
            </span>
          )}
          {row.source === "slot" && (
            <span className="inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase border border-[var(--ld-gold-deep)]/40 text-[var(--ld-gold-deep)]">
              Intro slot
            </span>
          )}
          {past && (
            <span className="inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase border border-red-700/40 text-red-700">
              Past due
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[var(--ld-ink-muted)]">
          <span>
            {fmtDate}
            {row.time ? ` · ${row.time}` : ""}
          </span>
          {row.email && (
            <a
              href={`mailto:${row.email}`}
              className="inline-flex items-center gap-1 hover:text-[var(--ld-gold-deep)] transition-colors"
            >
              <Mail size={12} strokeWidth={1.6} />
              {row.email}
            </a>
          )}
          {row.phone && (
            <a
              href={`tel:${row.phone}`}
              className="inline-flex items-center gap-1 hover:text-[var(--ld-gold-deep)] transition-colors"
            >
              <Phone size={12} strokeWidth={1.6} />
              {row.phone}
            </a>
          )}
        </div>
        {row.completed && row.completed_at && (
          <p className="mt-1 text-[11px] tracking-[0.12em] uppercase text-[var(--ld-ink-muted)]">
            Completed · {new Date(row.completed_at).toLocaleDateString()}
          </p>
        )}

        {/* Notes */}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1">
            <label
              className="font-accent text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]"
              htmlFor={`notes-${row.source}-${row.source_id}`}
            >
              Notes
            </label>
            <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)] h-3">
              {saving ? "Saving…" : savedTick ? "Saved" : ""}
            </span>
          </div>
          <textarea
            id={`notes-${row.source}-${row.source_id}`}
            data-testid={`consultation-notes-${row.source}-${row.source_id}`}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={handleBlur}
            placeholder="Impressions, follow-ups, anything to remember…"
            rows={2}
            className="w-full bg-transparent border border-[var(--ld-ink)]/15 px-3 py-2 text-[13px] leading-[1.6] text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)]/70 focus:outline-none focus:border-[var(--ld-gold-deep)]/60 transition-colors resize-y"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5 items-end">
        <button
          data-testid={`consultation-complete-${row.source}-${row.source_id}`}
          onClick={() => onToggle(row)}
          className={`opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border px-3 py-1.5 ${
            row.completed
              ? "border-[var(--ld-ink)]/30 text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)]"
              : "border-[var(--ld-ink)]/30 hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)]"
          }`}
        >
          {row.completed ? "Reopen" : "Mark complete"}
        </button>
        {onDelete && (
          <button
            data-testid={`consultation-delete-${row.source}-${row.source_id}`}
            onClick={() => onDelete(row)}
            title="Delete this consultation"
            className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-red-700/40 text-red-700 px-3 py-1.5 hover:bg-red-700/5"
          >
            <X size={11} strokeWidth={1.8} />
            Delete
          </button>
        )}
      </div>
    </div>
  );
};

const ConsultationPanel = ({
  consultations,
  loading,
  onToggle,
  onNotes,
  onDelete,
  onRefresh,
}) => {
  const [subtab, setSubtab] = useState("pending");

  const buckets = useMemo(() => {
    const out = {
      pending: [],
      "completed-vendors": [],
      "completed-couples": [],
    };
    consultations.forEach((c) => {
      if (!c.completed) {
        out.pending.push(c);
      } else if (c.role === "vendor") {
        out["completed-vendors"].push(c);
      } else {
        out["completed-couples"].push(c);
      }
    });
    return out;
  }, [consultations]);

  const visible = buckets[subtab] || [];

  return (
    <div data-testid="section-consultation">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
            Workspace
          </span>
          <h2 className="font-serif-display mt-3 text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
            Consultation
          </h2>
          <p className="mt-2 text-sm text-[var(--ld-ink-muted)] max-w-2xl">
            Every booked vendor intro slot, couple intro slot, and scheduled
            couple consultation lands here automatically. Tick one off and it
            moves to the appropriate completed sub-tab.
          </p>
        </div>
        <button
          data-testid="consultation-refresh"
          onClick={onRefresh}
          className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] transition-colors"
        >
          <RefreshCcw size={14} strokeWidth={1.6} />
          Refresh
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="mt-8 flex flex-wrap gap-2 text-[11px] tracking-[0.18em] uppercase border-b border-[var(--ld-border)]/70 pb-3">
        {CONSULTATION_SUBTABS.map((t) => {
          const active = subtab === t.id;
          const count = buckets[t.id]?.length || 0;
          return (
            <button
              key={t.id}
              data-testid={`consultation-subtab-${t.id}`}
              onClick={() => setSubtab(t.id)}
              className={`px-3 py-1.5 border transition-colors ${
                active
                  ? "bg-[var(--ld-ink)] text-[var(--ld-ivory)] border-[var(--ld-ink)]"
                  : "border-[var(--ld-ink)]/25 text-[var(--ld-ink-muted)] hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)]"
              }`}
            >
              {t.label} · {count}
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="mt-6 border border-[var(--ld-border)]/80">
        {loading && (
          <div className="px-5 py-12 text-center text-[var(--ld-ink-muted)] text-sm">
            Loading consultations…
          </div>
        )}
        {!loading && visible.length === 0 && (
          <div className="px-5 py-12 text-center text-[var(--ld-ink-muted)] text-sm">
            {subtab === "pending"
              ? "No pending consultations. Bookings will appear here automatically."
              : "Nothing here yet."}
          </div>
        )}
        {!loading &&
          visible.map((row) => (
            <ConsultationRow
              key={`${row.source}-${row.source_id}`}
              row={row}
              onToggle={onToggle}
              onNotes={onNotes}
              onDelete={onDelete}
            />
          ))}
      </div>
    </div>
  );
};

export default ConsultationPanel;
